import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Shared @dnd-kit helpers used by the sortable field editors
// (CustomUrlColumns, SortingParameters, ClassificationFields,
// OptionValuesFields, PfFieldsSelector).

// The stable identity of a sortable item: its `id`, falling back to `key`.
// Uses `??` (not `||`) so a legitimately falsy id like `0` is not skipped.
export const keyOf = (item) => item.id ?? item.key;

// Reorders `items` so the item identified by `activeId` takes the slot of
// `overId`. Items are matched on `id`, falling back to `key`. No-op when the
// ids are equal or either is not found.
export const reorderById = (items, activeId, overId) => {
  if (activeId === overId) return items;
  const oldIndex = items.findIndex((it) => keyOf(it) === activeId);
  const newIndex = items.findIndex((it) => keyOf(it) === overId);
  if (oldIndex < 0 || newIndex < 0) return items;
  return arrayMove(items, oldIndex, newIndex);
};

// Standard @dnd-kit `onDragEnd` for class components that hold a sortable list
// in `state[listKey]`. Reorders that list by the dragged/dropped ids and writes
// it back. `callback` is forwarded as the optional `setState` callback (used by
// OptionValuesFields to recalculate positions after a reorder).
export const reorderInState = ({ active, over }, component, listKey, callback) => {
  if (!over || active.id === over.id) return;
  component.setState(
    { [listKey]: reorderById(component.state[listKey], active.id, over.id) },
    callback,
  );
};

// Pointer + keyboard sensors with the project-standard 4px drag threshold.
export const useDndSensors = () => useSensors(
  useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
);

// Wires a single sortable row to @dnd-kit: returns the node ref, drag
// attributes/listeners, `isDragging`, and the transform/transition style every
// row needs. `options` are forwarded to `useSortable` (e.g. `data` for the
// cross-container PfFieldsSelector rows). Callers own the surrounding markup.
export const useSortableRow = (id, options) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, ...options });
  return {
    setNodeRef,
    attributes,
    listeners,
    isDragging,
    style: { transform: CSS.Transform.toString(transform), transition },
  };
};

// The shared row skeleton: a sortable wrapper with a drag handle, then the
// editor-specific child. Only the wrapper `className`/`style` and the handle's
// `className`/`style`/icon legitimately vary between editors.
export const SortableListRow = ({
  id,
  className = 'row mb-10',
  style: extraStyle,
  handleClassName = 'col-xs-1 col-drag-handle',
  handleStyle,
  iconClassName = 'icon-three-bars text-muted pt-10 cursor-move',
  children,
}) => {
  const {
    setNodeRef, attributes, listeners, style,
  } = useSortableRow(id);

  return (
    <div ref={setNodeRef} style={{ ...style, ...extraStyle }} className={className} {...attributes}>
      <div className={handleClassName} style={handleStyle} {...listeners}>
        <i className={iconClassName} title="Reorder"></i>
      </div>
      {children}
    </div>
  );
};

// The shared DndContext + SortableContext wrapper. Callers supply the sensors,
// the ordered item ids, the `onDragEnd` handler, and the rows as children.
export const SortableList = ({
  sensors, items, onDragEnd, children,
}) => (
  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
    <SortableContext items={items} strategy={verticalListSortingStrategy}>
      <div>{children}</div>
    </SortableContext>
  </DndContext>
);

// HOC that supplies `useDndSensors()` to class components (which can't call
// hooks directly) via a `__sensors` prop.
export const withDndSensors = (Component) => {
  const Wrapped = (props) => {
    const sensors = useDndSensors();
    return <Component {...props} __sensors={sensors} />;
  };
  Wrapped.displayName = `withDndSensors(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
};
